'use client';

import { useState } from 'react';
import { ArrowUpRight, ArrowRight } from '@phosphor-icons/react';
import type { PublicPromotion } from '@/lib/promotion-content';
import { useMoneyRules, type StatusRollType } from '@/lib/money-rules';
import { MobileStatusShowcase } from './mobile-status-showcase';
import { MobileLoyaltyPass } from './mobile-loyalty-pass';
import { OfferCatalogPhoto, useOfferGift } from './offer-catalog-photo';
import { usePhotoEdges } from './offer-product';
import { getOfferCampaign, getOfferPhoto } from './offer-photo-source';
import './mobile-offers-experience.css';

export interface OfferPreviewSelection {
  tier?: StatusRollType;
  level?: number;
}
type OpenOffer = (
  offer: PublicPromotion,
  trigger: HTMLButtonElement,
  preview?: OfferPreviewSelection
) => void;
interface Props {
  offers: PublicPromotion[];
  onOpen: OpenOffer;
}
const rubles = (value: number) => new Intl.NumberFormat('ru-RU').format(value);

export function MobileOffersExperience({ offers, onOpen }: Props) {
  return (
    <div className="mobile-offers-experience">
      <div className="mobile-offers-chapters">
        {offers.map((offer) => (
          <div className="mobile-offer-chapter" data-mobile-offer={offer.slug} key={offer.id}>
            {offer.detail_key === 'firstOrder' ? (
              <WelcomeGift offer={offer} onOpen={onOpen} />
            ) : offer.detail_key === 'statusRolls' ? (
              <MobileStatusShowcase
                offer={offer}
                onOpen={(trigger, tier) => onOpen(offer, trigger, { tier })}
              />
            ) : offer.detail_key === 'loyalty' ? (
              <MobileLoyaltyPass
                offer={offer}
                onOpen={(trigger, level) => onOpen(offer, trigger, { level })}
              />
            ) : offer.detail_key === 'review' ? (
              <ReviewGift offer={offer} onOpen={onOpen} />
            ) : (
              <OtherOffer offer={offer} onOpen={onOpen} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function WelcomeGift({ offer, onOpen }: { offer: PublicPromotion; onOpen: OpenOffer }) {
  const { first_order } = useMoneyRules();
  const gift = useOfferGift(first_order.product_id);
  const [failedCover, setFailedCover] = useState<string | null>(null);
  const approvedCover = getOfferCampaign(gift.data)?.mobile;
  const cover = approvedCover !== failedCover ? approvedCover : undefined;
  const scene = usePhotoEdges(cover ? undefined : getOfferPhoto(gift.data));
  return (
    <section
      className={`mobile-welcome${cover ? ' mobile-welcome--campaign' : ''}`}
      style={cover ? undefined : scene}
      aria-labelledby={`mobile-offer-${offer.id}`}
    >
      {cover && (
        <picture className="mobile-welcome-cover">
          <img
            src={cover}
            alt=""
            width={1024}
            height={2048}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            onError={() => setFailedCover(cover)}
          />
        </picture>
      )}
      <div className="mobile-welcome-title">
        <p className="mobile-chapter-eyebrow">Для новых гостей</p>
        <h2 id={`mobile-offer-${offer.id}`}>{offer.title}</h2>
      </div>
      <div className="mobile-welcome-scene">
        {!cover && (
          <OfferCatalogPhoto
            product={gift.data}
            loading={gift.isLoading}
            className="mobile-welcome-product"
            eager
          />
        )}
        <div className="mobile-welcome-ticket">
          <span>К первому заказу</span>
          <strong>
            <small>от</small> {rubles(first_order.min_sum)} <span>₽</span>
          </strong>
          <span className="mobile-ticket-bottom">
            Ролл — в подарок <ArrowUpRight size={16} aria-hidden="true" />
          </span>
        </div>
      </div>
      <div className="mobile-welcome-footer">
        <p>{offer.description}</p>
        <button
          className="mobile-welcome-action"
          aria-haspopup="dialog"
          onClick={(event) => onOpen(offer, event.currentTarget)}
        >
          Как получить подарок{' '}
          <span>
            <ArrowUpRight size={22} aria-hidden="true" />
          </span>
        </button>
      </div>
    </section>
  );
}

function ReviewGift({ offer, onOpen }: { offer: PublicPromotion; onOpen: OpenOffer }) {
  return (
    <section className="mobile-review-note" aria-labelledby={`mobile-offer-${offer.id}`}>
      <p className="mobile-chapter-eyebrow">Нам важно ваше мнение</p>
      <div className="mobile-review-headline">
        <h2 id={`mobile-offer-${offer.id}`}>{offer.title}</h2>
        <div className="mobile-review-glass" aria-hidden="true">
          <img
            src="/storefront/offers-v2-review.webp"
            alt=""
            width={1536}
            height={1024}
            loading="lazy"
          />
        </div>
      </div>
      <p className="mobile-review-description">{offer.description}</p>
      <button
        className="mobile-review-action"
        aria-haspopup="dialog"
        onClick={(event) => onOpen(offer, event.currentTarget)}
      >
        Как получить ролл за отзыв <ArrowRight size={21} aria-hidden="true" />
      </button>
    </section>
  );
}

function OtherOffer({ offer, onOpen }: { offer: PublicPromotion; onOpen: OpenOffer }) {
  const birthday = offer.detail_key === 'birthday';
  return (
    <section className="mobile-other-offer" aria-labelledby={`mobile-offer-${offer.id}`}>
      <h2 id={`mobile-offer-${offer.id}`}>{offer.title}</h2>
      <p>{offer.description}</p>
      {birthday && (
        <button onClick={(event) => onOpen(offer, event.currentTarget)} aria-haspopup="dialog">
          Открыть праздничный подарок <ArrowUpRight size={21} aria-hidden="true" />
        </button>
      )}
    </section>
  );
}
